<template>
    <FormSection title="Immunization" :full-height="false" :application-id="application">
        <template #content>
            <div class="row row pt-2 mx-3">
                <div class="col-12 d-flex justify-content-between mb-2">
                    <h6 class="fw-bold">Record of Vaccine Administration</h6>
                    <div v-if="!isAddVaccineRequested && !isAddDeclineRequested">
                        <button
                            class="btn btn-sm btn-primary me-2"
                            @click="openAddVaccineRecord()"
                            :disabled="isDownloadInProgress"
                        >
                            Add Vaccine Record
                        </button>
                        <button
                            class="btn btn-sm btn-primary me-2"
                            @click="openAddDeclinedRecord()"
                            :disabled="isDownloadInProgress"
                        >
                            Add Declined Vaccine Record
                        </button>
                        <button
                            class="btn btn-sm btn-primary"
                            @click="downloadVaccineRecords()"
                            :disabled="isDownloadInProgress"
                        >
                            <template v-if="isDownloadInProgress"> Downloading... </template>
                            <template v-else> Download </template>
                        </button>
                    </div>
                </div>
                <div
                    :class="`card px-lg-5 py-2 mb-2 w-100 h-100 animated ${
                        isAddVaccineRequested ? 'faster fadeInDown' : 'slow fadeOutUp'
                    }`"
                    v-if="isAddVaccineRequested"
                >
                    <h5 class="text-capitalize">Add Vaccine Record</h5>
                    <FormSection v-if="isAddVaccineInProgress">
                        <template #content>
                            <div class="p-5 d-flex flex-column text-center text-primary">
                                <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                                <div class="big-text mt-1">
                                    <b>Save Vaccine Record in Progress...</b>
                                </div>
                            </div>
                        </template>
                    </FormSection>
                    <template v-else>
                        <VaccineRecordForm
                            ref="vaccineForm"
                            :immunization="immunization"
                            :application="application"
                            :immunization-status-enum-map="immunizationStatusEnumMap"
                            :immunization-status-enum="immunizationStatusEnum"
                            :type-of-record-enum="typeOfRecordEnum"
                            :type-of-record-enum-map="typeOfRecordEnumMap"
                            v-if="isAddVaccineRequested"
                        />
                        <div class="text-center mt-2">
                            <button type="button" class="btn btn-primary btn-md me-2" @click="saveNewVaccineRecord()">
                                Save
                            </button>
                            <button
                                type="button"
                                class="btn btn-primary btn-md me-2"
                                @click="saveNewVaccineRecord(true)"
                            >
                                Save and Add Another
                            </button>
                            <button
                                type="button"
                                class="btn btn-outline-secondary btn-md"
                                @click="closeAddVaccineRecord()"
                            >
                                Cancel
                            </button>
                        </div>
                    </template>
                </div>
                <div
                    :class="`card px-lg-5 py-2 mb-2 w-100  h-100 animated ${
                        isAddDeclineRequested ? 'faster fadeInDown' : 'slow fadeOutUp'
                    }`"
                    v-if="isAddDeclineRequested"
                >
                    <h5 class="text-capitalize">Add Declined Vaccine Record</h5>
                    <FormSection v-if="isAddDeclineInProgress">
                        <template #content>
                            <div class="p-5 d-flex flex-column text-center text-primary">
                                <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                                <div class="big-text mt-1">
                                    <b>Save Declined Vaccine Record in Progress...</b>
                                </div>
                            </div>
                        </template>
                    </FormSection>
                    <template v-else>
                        <DeclinedVaccineForm
                            ref="declinedVaccineForm"
                            :immunization="declinedImmunization"
                            :application="application"
                            :declined-value-set="declinedValueSet"
                            :type-of-record-enum="typeOfRecordEnum"
                            v-if="isAddDeclineRequested"
                        />
                        <div class="text-center mt-2" v-if="!isAddDeclineInProgress">
                            <button
                                type="button"
                                class="btn btn-primary btn-md me-2"
                                @click="saveDeclinedVaccineRecord()"
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                class="btn btn-primary btn-md me-2"
                                @click="saveDeclinedVaccineRecord(true)"
                            >
                                Save and Add Another
                            </button>
                            <button
                                type="button"
                                class="btn btn-outline-secondary btn-md"
                                @click="closeAddDeclinedRecord()"
                            >
                                Cancel
                            </button>
                        </div>
                    </template>
                </div>
                <div class="col-12 px-0">
                    <Pagination
                        ref="immunizationPaginationInstance"
                        :pageChanged="pageChangedCallback"
                        :is-loading="isLoading"
                    >
                        <table class="table table-responsive-md border border-gray">
                            <thead>
                                <tr class="bg-table-header">
                                    <th>Vaccine Name</th>
                                    <th>Date of Vaccination</th>
                                    <th>Type of Record</th>
                                    <th>Date Documented</th>
                                    <th>Administered By</th>
                                    <th>Immunization Status</th>
                                    <th>Reason Not Performed</th>
                                    <th>Comments</th>
                                    <th class="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <!-- LOADING -->
                                <tr v-if="isLoading" v-for="i of loadingCount" class="table-loading">
                                    <td v-for="i of columns">
                                        <span class="shimmer-loading"></span>
                                    </td>
                                </tr>
                                <!-- NO RESULTS -->
                                <tr v-if="!isLoading && !isError && !hasData" class="table-empty">
                                    <td :colspan="columns">
                                        <h3>No Vaccine Records to Display</h3>
                                    </td>
                                </tr>
                                <!-- ERROR -->
                                <tr v-if="!isLoading && isError" class="table-error">
                                    <td :colspan="columns">
                                        <ErrorMessageHandler title="Vaccine Records" :try-again="reset">
                                        </ErrorMessageHandler>
                                    </td>
                                </tr>
                            </tbody>
                            <template v-if="!isLoading && !isError && hasData">
                                <ImmunizationRow
                                    v-for="(immunizationRecord, index) in page.Items"
                                    :immunization="immunizationRecord"
                                    :declined-value-set="declinedValueSet"
                                    :columns="columns"
                                    :page-reset="reset"
                                    :patient-id="patientId"
                                    :status-enum-map="immunizationStatusEnumMap"
                                    :immunization-status-enum="immunizationStatusEnum"
                                    :type-of-record-enum-map="typeOfRecordEnumMap"
                                    :type-of-record-enum="typeOfRecordEnum"
                                    :accountId="accountId"
                                    :application="application"
                                />
                            </template>
                        </table>
                    </Pagination>
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script src="./Immunization.ts"></script>
